import React from 'react';
import { Alert, StyleSheet, Text } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { CalcScreen, CategoryCard, ListRow } from '../../components/shell';
import { C, FONT, SP } from '../../theme/tokens';

type Calc = { label: string; description: string };
type ElectricalCategory = {
  title: string;
  description: string;
  emoji: string;
  calculations: Calc[];
};

const ELECTRICAL_CATEGORIES: ElectricalCategory[] = [
  {
    title: 'Power',
    description: 'Power calculations and relationships',
    emoji: '⚡',
    calculations: [
      { label: 'Power Calculations', description: 'Solve watts from volts, amps, and power factor' },
    ],
  },
  {
    title: 'Volts/Amps/Ohms',
    description: "Electrical relationships and Ohm's Law",
    emoji: '🔌',
    calculations: [
      { label: 'Electrical Relationships', description: 'Ohm’s Law: solve volts, amps, or resistance' },
    ],
  },
  {
    title: 'Motor Calculations',
    description: 'Motor performance and efficiency calculations',
    emoji: '🔧',
    calculations: [
      { label: 'Brake Horsepower', description: 'Motor BHP from measured volts, amps, PF, and efficiency' },
      { label: 'FLA', description: 'Full-load amps from motor HP, voltage, PF, and efficiency' },
      { label: 'Fan Horsepower', description: 'Air-side fan HP from CFM, static pressure, and efficiency' },
    ],
  },
  {
    title: 'Electrical Systems',
    description: 'System-level electrical calculations',
    emoji: '🏗️',
    calculations: [
      { label: 'Total Resistance', description: 'Combine series and parallel resistors into a total' },
      { label: 'Primary/Secondary', description: 'Transformer primary/secondary volts and amps from turns ratio' },
      { label: 'Voltage Drop', description: 'Conductor voltage drop from length, amps, and wire size' },
    ],
  },
];

const SCREEN_MAP: Record<string, string> = {
  'Power Calculations': 'ElectricalPowerPowerCalculations',
  'Electrical Relationships': 'ElectricalVoltsAmpsOhms',
  'Brake Horsepower': 'ElectricalMotorCalculationsBrakeHorsepower',
  'FLA': 'ElectricalMotorFLA',
  'Fan Horsepower': 'ElectricalMotorCalculationsFanHorsepower',
  'Total Resistance': 'ElectricalElectricalSystemsTotalResistance',
  'Primary/Secondary': 'ElectricalElectricalSystemsPrimarySecondary',
  'Voltage Drop': 'ElectricalElectricalSystemsVoltageDrop',
};

const TABElectricalScreen = () => {
  const navigation = useNavigation();

  const handleCalculationPress = (calculation: string) => {
    const screenName = SCREEN_MAP[calculation];
    if (screenName) {
      navigation.navigate(screenName as never);
    } else {
      Alert.alert('Coming Soon', `${calculation} calculator will be available soon!`);
    }
  };

  return (
    <CalcScreen title="Electrical Calculations" subtitle="TAB · Electrical">
      <Text style={styles.sectionLabel}>Electrical Categories</Text>
      {ELECTRICAL_CATEGORIES.map((category) => (
        <CategoryCard
          key={category.title}
          emoji={category.emoji}
          title={category.title}
          description={category.description}
        >
          {category.calculations.map((calculation, i) => (
            <ListRow
              key={calculation.label}
              index={i}
              label={calculation.label}
              description={calculation.description}
              onPress={() => handleCalculationPress(calculation.label)}
            />
          ))}
        </CategoryCard>
      ))}
    </CalcScreen>
  );
};

const styles = StyleSheet.create({
  sectionLabel: {
    fontFamily: FONT.sans,
    fontSize: 12,
    color: C.dim,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: SP.sm,
  },
});

export default TABElectricalScreen;
